import CrossReference from "@/components/links/CrossReference/CrossReference";
import NoImpactBanner from "@/components/NoImpactBanner/NoImpactBanner";
import useTableState from "@/components/Table/hooks/useTableState";
import Table, { Column } from "@/components/Table/Table";
import useTranslation from "@/hooks/useTranslation";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import { useDashboardOrderCounts } from "@/pages/dashboard/hooks/usePoSo";
import {
  MaterialImpactPerProductMaterial,
  ProductBy,
  useProductMaterialImpactPerMaterial,
} from "@/pages/dashboard/hooks/useProductImpact";
import { formatAmountWithUnit } from "@/pages/dashboard/utils/formatters";
import { ImpactMetric } from "@/pages/dashboard/utils/types";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import DashboardTableShare from "@/components/DashboardTableShare/DashboardTableShare";

export default function MaterialImpactPerMaterial({
  metric,
  productId,
  by,
}: {
  metric: ImpactMetric;
  productId: number;
  by: ProductBy;
}) {
  const t = useTranslation().general;

  const { pagination, sort, search, filter, onSearchFilterSortPaginate, setSort } = useTableState({
    defaultPagination: { page: 1, pageSize: 10 },
    defaultSort: {
      key: metric.key,
      order: "descend",
    },
  });

  const columns: Column<MaterialImpactPerProductMaterial>[] = [
    {
      label: t.material,
      key: "name",
      sortable: true,
      maxWidth: "2fr",
      searchable: true,
      renderer: (material) => (
        <CrossReference to={`/materials/${material.id}`}>{material.name}</CrossReference>
      ),
    },
    {
      label: "share",
      key: "percentage",
      renderer: (record) => {
        return (
          <DashboardTableShare
            total={record.impact[metric.key]}
            value={data?.total_impact[metric.key]}
          />
        );
      },
    },
    {
      label: t.quantity,
      key: "quantity",
      sortable: true,
      renderer: (record) => {
        return `${formatAmountWithUnit(record.quantity, record.unit, 4)}`;
      },
    },
    {
      label: metric.label,
      key: metric.key,
      sortable: true,
      minWidth: "220px",
      renderer: (record) => {
        return `${metric.formatter(record.impact[metric.key] || 0)}`;
      },
    },
    {
      label: "Supplier",
      key: "supplier_name",
      sortable: true,
      maxWidth: "2fr",
      searchable: true,
    },
    {
      label: "address",
      key: "supplier_address",
      sortable: true,
      maxWidth: "2fr",
      searchable: true,
    },
  ];

  const { data, isLoading, isFetching } = useProductMaterialImpactPerMaterial({
    pagination,
    filter,
    search,
    sort,
    productId,
    by,
    metric: metric.key,
  });

  useEffect(() => {
    setSort({ key: metric.key, order: "descend" });
  }, [metric]);

  const { data: orderCounts } = useDashboardOrderCounts({ productId });
  const { year } = useDashboardYear();

  if (orderCounts?.po === 0) {
    return (
      <NoImpactBanner title={`No material impact for this product in ${year}`}>
        <span>
          This product was not found in any <strong>purchase orders</strong> in {year}, so no impact
          could be calculated. Navigate to a different year, or upload{" "}
          <Link to="/orders/purchase_orders">purchase orders</Link>.
        </span>
      </NoImpactBanner>
    );
  }

  if (data && data.total_impact[metric.key] === 0) {
    return (
      <NoImpactBanner
        title={`No material impact`}
        size="small"
      >
        <span>
          Check if you've uploaded a{" "}
          <Link to={`/products/${productId}/bom`}>bill of materials</Link> (BOM) for this product
          and have matched all materials of that BOM with an impact reference.
        </span>
      </NoImpactBanner>
    );
  }

  return (
    <Table
      hideRaster={true}
      type={"grey"}
      filter={filter}
      sort={sort}
      search={search}
      columns={columns}
      data={data?.materials}
      loading={isLoading || isFetching}
      onSearchFilterSortPaginate={onSearchFilterSortPaginate}
      pagination={pagination}
      totalRows={data?.total_count}
      rowKey={"rowId"}
    />
  );
}
